import axios, { type AxiosError } from 'axios';

/** Known backend error codes mapped to Turkish user-facing messages. */
const ERROR_CODE_MESSAGES: Record<string, string> = {
  ACCOUNT_BANNED: 'Hesabınız askıya alınmıştır. Destek ekibiyle iletişime geçin.',
  CORPORATE_NOT_APPROVED: 'Kurumsal hesabınız henüz onaylanmadı.',
  EMAIL_NOT_CONFIRMED: 'E-posta adresiniz henüz doğrulanmamış. Lütfen gelen kutunuzu kontrol edin.',
  INVALID_CREDENTIALS: 'E-posta veya şifre hatalı. Lütfen bilgilerinizi kontrol edin.',
  USER_NOT_FOUND: 'Bu e-posta adresiyle kayıtlı bir hesap bulunamadı.',
  EMAIL_ALREADY_EXISTS: 'Bu e-posta adresi zaten kullanılıyor.',
  VALIDATION_FAILED: 'Girdiğiniz bilgiler geçersiz. Lütfen formu kontrol edin.',
};

/** Substring matches for raw English/technical backend messages. */
const MESSAGE_PATTERN_MAP: { pattern: RegExp; message: string }[] = [
  { pattern: /invalid credentials|wrong password|unauthorized/i, message: 'E-posta veya şifre hatalı. Lütfen bilgilerinizi kontrol edin.' },
  { pattern: /email.*not.*confirm/i, message: 'E-posta adresiniz henüz doğrulanmamış. Lütfen gelen kutunuzu kontrol edin.' },
  { pattern: /already exists|duplicate/i, message: 'Bu kayıt zaten mevcut.' },
  { pattern: /not found/i, message: 'İstenen kayıt bulunamadı.' },
  { pattern: /forbidden|access denied/i, message: 'Bu işlem için yetkiniz bulunmuyor.' },
  { pattern: /network error|failed to fetch|timeout/i, message: 'Bağlantı hatası. İnternet bağlantınızı kontrol edip tekrar deneyin.' },
  { pattern: /internal server error|500/i, message: 'Sunucuda bir hata oluştu. Lütfen daha sonra tekrar deneyin.' },
  { pattern: /bad request|400/i, message: 'Gönderilen bilgiler geçersiz. Lütfen formu kontrol edin.' },
  { pattern: /too many requests|429/i, message: 'Çok fazla istek gönderildi. Lütfen bir süre bekleyip tekrar deneyin.' },
];

const STATUS_MESSAGES: Record<number, string> = {
  400: 'Gönderilen bilgiler geçersiz. Lütfen formu kontrol edin.',
  401: 'Oturumunuz sonlanmış olabilir. Lütfen tekrar giriş yapın.',
  403: 'Bu işlem için yetkiniz bulunmuyor.',
  404: 'İstenen kayıt bulunamadı.',
  412: 'Bu işlem için önce hesabınıza şifre oluşturmalısınız.',
  409: 'Bu kayıt zaten mevcut veya çakışma oluştu.',
  422: 'Girdiğiniz bilgiler geçersiz. Lütfen formu kontrol edin.',
  429: 'Çok fazla istek gönderildi. Lütfen bir süre bekleyip tekrar deneyin.',
  500: 'Sunucuda bir hata oluştu. Lütfen daha sonra tekrar deneyin.',
  502: 'Sunucuya ulaşılamıyor. Lütfen daha sonra tekrar deneyin.',
  503: 'Hizmet geçici olarak kullanılamıyor. Lütfen daha sonra tekrar deneyin.',
};

type ApiErrorBody = {
  message?: string | null;
  code?: string | null;
  title?: string | null;
  errors?: Record<string, string[] | string> | null;
};

function extractRawMessage(body: unknown): string | null {
  if (body == null) return null;
  if (typeof body === 'string' && body.trim()) return body.trim();
  if (typeof body !== 'object') return null;

  const record = body as ApiErrorBody;
  if (record.message?.trim()) return record.message.trim();
  if (record.title?.trim()) return record.title.trim();

  if (record.errors && typeof record.errors === 'object') {
    const first = Object.values(record.errors)[0];
    if (Array.isArray(first) && first[0]?.trim()) return first[0].trim();
    if (typeof first === 'string' && first.trim()) return first.trim();
  }

  return null;
}

function mapRawMessage(raw: string): string | null {
  const trimmed = raw.trim();
  if (/^[a-z]+(?:_[a-z]+)+$/i.test(trimmed)) {
    return null;
  }
  if (/exception|stack trace|\sat\s/i.test(trimmed)) {
    return null;
  }
  for (const { pattern, message } of MESSAGE_PATTERN_MAP) {
    if (pattern.test(trimmed)) return message;
  }
  if (/^[A-Za-z\s:.,!?\-'"]+$/.test(trimmed) && trimmed.length > 80) {
    return null;
  }
  return trimmed;
}

/**
 * Converts raw API/Axios errors into concise Turkish messages for end users.
 */
export function getFriendlyApiErrorMessage(
  error: unknown,
  fallback = 'Bir hata oluştu. Lütfen tekrar deneyin.'
): string {
  if (axios.isAxiosError(error)) {
    return getFriendlyAxiosErrorMessage(error, fallback);
  }
  if (error instanceof Error && error.message.trim()) {
    const mapped = mapRawMessage(error.message);
    return mapped ?? fallback;
  }
  return fallback;
}

export function getFriendlyAxiosErrorMessage(
  error: AxiosError,
  fallback = 'Bir hata oluştu. Lütfen tekrar deneyin.'
): string {
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return 'İstek zaman aşımına uğradı. Lütfen tekrar deneyin.';
    }
    return 'Bağlantı hatası. İnternet bağlantınızı kontrol edip tekrar deneyin.';
  }

  const status = error.response.status;
  const body = error.response.data;
  const record = typeof body === 'object' && body !== null ? (body as ApiErrorBody) : null;

  if (record?.code && ERROR_CODE_MESSAGES[record.code]) {
    return ERROR_CODE_MESSAGES[record.code];
  }

  const raw = extractRawMessage(body);
  if (raw) {
    const mapped = mapRawMessage(raw);
    if (mapped) return mapped;
  }

  return STATUS_MESSAGES[status] ?? fallback;
}

/** Attach a user-friendly message to an Axios error for downstream catch blocks. */
export function attachFriendlyApiErrorMessage(error: AxiosError): AxiosError {
  const friendly = getFriendlyAxiosErrorMessage(error);
  (error as AxiosError & { friendlyMessage?: string }).friendlyMessage = friendly;
  return error;
}

export function readFriendlyApiErrorMessage(error: unknown, fallback?: string): string {
  if (axios.isAxiosError(error)) {
    const withFriendly = error as AxiosError & { friendlyMessage?: string };
    if (withFriendly.friendlyMessage) return withFriendly.friendlyMessage;
    return getFriendlyAxiosErrorMessage(error, fallback);
  }
  return getFriendlyApiErrorMessage(error, fallback);
}
